import React, { useState, useEffect, useRef } from 'react';
import { useCourseData } from '../hooks/useCourseData';
import { useFont } from '../../Design/FontContext';
import { formatModuleDuration, calculateTotalDuration, getCourseTypeInfo } from '../hooks/useCourseData';
import { getTextEffectStyles } from '../utils/textEffectsUtils';
import { getButtonEffectStyles } from '../utils/buttonEffectsUtils';
import { getResponsiveFontSize } from '../utils/responsiveFontUtils';
import DeleteModal from './DeleteModal';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './WidgetStyle.css';
import './ResponsiveFonts.css';
import './CourseInfoWidgetGuide.css';
import '../WidgetEditComponents/TextEffectsAnimations.css';

function appendPx(value) {
    if (typeof value === 'number') return `${value}px`;
    return value;
}

const CourseInfoWidget = ({ data, isSelected, onRemove, isEditMode, onUpdate, onSelectWidget }) => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isMobileLayout, setIsMobileLayout] = useState(false);
    const [currentBreakpoint, setCurrentBreakpoint] = useState('normal');
    const [refreshCounter, setRefreshCounter] = useState(0);
    const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);
    const containerRef = useRef(null);
    const { courseData, loading, error } = useCourseData(data.courseId, refreshCounter);
    
    // Firebase Auth & Functions
    const auth = getAuth();
    const user = auth.currentUser;
    const functions = getFunctions();
    
    const deleteLogo = '/img/delete.svg';
    const fonts = useFont();
    
    // Inline Editing System
    const [isInlineEditing, setIsInlineEditing] = useState(null);
    const [inlineValue, setInlineValue] = useState('');
    const widgetRef = useRef(null);

    // Responsive breakpoint detection
    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            if (width < 768) {
                setCurrentBreakpoint('mobile');
                setIsMobileLayout(true);
            } else if (width > 1200) {
                setCurrentBreakpoint('big');
                setIsMobileLayout(false);
            } else {
                setCurrentBreakpoint('normal');
                setIsMobileLayout(false);
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Course refresh handlers
    useEffect(() => {
        const handleFocus = () => {
            setRefreshCounter(prev => prev + 1);
        };

        const handleVisibilityChange = () => {
            if (!document.hidden) {
                setRefreshCounter(prev => prev + 1);
            }
        };

        const handleCourseUpdate = (event) => {
            if (event.detail.courseId === data.courseId) {
                setRefreshCounter(prev => prev + 1);
            }
        };

        window.addEventListener('focus', handleFocus);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('courseUpdated', handleCourseUpdate);

        return () => {
            window.removeEventListener('focus', handleFocus);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('courseUpdated', handleCourseUpdate);
        };
    }, [data.courseId]);

    useEffect(() => {
        setRefreshCounter(prev => prev + 1);
    }, [data.courseId]);

    // Inline editing handlers
    const handleInlineEdit = (field, currentValue) => {
        if (!isEditMode) return;
        setIsInlineEditing(field);
        setInlineValue(currentValue);
    };

    const handleInlineSave = () => {
        if (onUpdate && isInlineEditing) {
            onUpdate(data.id, { [isInlineEditing]: inlineValue });
        }
        setIsInlineEditing(null);
        setInlineValue('');
    };

    const handleInlineCancel = () => {
        setIsInlineEditing(null);
        setInlineValue('');
    };

    // Outside click detection for inline editing
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (widgetRef.current && !widgetRef.current.contains(event.target) && isInlineEditing) {
                handleInlineSave();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isInlineEditing, inlineValue]);

    // Style functions
    function getBorderStyle() {
        if (isEditMode) {
            return isSelected ? '2px dashed #000' : '2px dashed transparent';
        }
        return data.border ? `${data.borderWidth}px solid ${data.borderColor}` : 'none';
    }

    const getResponsiveStyle = (baseProperty, suffix = '') => {
        return getResponsiveFontSize(data, baseProperty, currentBreakpoint, suffix);
    };

    const handleDelete = (event) => {
        onRemove(event, data.id);
        setShowDeleteModal(false);
    };

    // Helper Functions for Course Purchase
    const isFreeProduct = () => {
        return courseData?.price === 0 || courseData?.price === '0' || !courseData?.price;
    };

    // Handle Stripe checkout for paid courses
    const handleStripeCheckout = async () => {
        if (!courseData || isFreeProduct() || isProcessingCheckout) return;

        // Check if course has Stripe configuration
        if (!courseData.stripePriceId) {
            alert('This course is not properly configured for payments. Please contact support.');
            return;
        }

        setIsProcessingCheckout(true);

        try {
            console.log('🎓 Creating course checkout session...');
            console.log('Course ID:', data.courseId);
            console.log('Stripe Price ID:', courseData.stripePriceId);

            const createProductCheckoutSession = httpsCallable(functions, 'createProductCheckoutSession');
            
            const { data: sessionData } = await createProductCheckoutSession({
                userId: user ? user.uid : null,
                productId: data.courseId,
                priceId: courseData.stripePriceId,
                successUrl: courseData.successUrl || `${window.location.origin}/success?session_id={CHECKOUT_SESSION_ID}`,
                cancelUrl: courseData.cancelUrl || window.location.href,
                customerEmail: user ? user.email : null
            });

            console.log('Checkout session created:', sessionData);

            // Redirect to Stripe Checkout
            if (sessionData.checkoutUrl) {
                window.location.href = sessionData.checkoutUrl;
            } else {
                // Fallback to Stripe's redirect method
                const stripe = await import('@stripe/stripe-js').then(module => 
                    module.loadStripe('pk_live_51JtPI5EK7wspITU4metLteANz6fXOGY4vIvFtRdJYYg3oYt0W9M0mipHroPArKiCQPaAeU7sZvgH17wU0FMYN69200bLf83Oq7')
                );
                
                const { error } = await stripe.redirectToCheckout({ 
                    sessionId: sessionData.sessionId 
                });

                if (error) {
                    console.error("Stripe checkout error:", error);
                    alert('Payment processing error. Please try again.');
                }
            }
        } catch (error) {
            console.error("Checkout session error:", error);
            alert('Unable to process payment. Please try again or contact support.');
        } finally {
            setIsProcessingCheckout(false);
        }
    };

    // Image Filter Effects (same as EventInfoWidget)
    const getImageEffect = (effect) => {
        switch (effect) {
            case 'blur': return { filter: 'blur(3px)', gradient: null };
            case 'blur-light': return { filter: 'blur(1px)', gradient: null };
            case 'blur-heavy': return { filter: 'blur(5px)', gradient: null };
            case 'grayscale': return { filter: 'grayscale(100%)', gradient: null };
            case 'sepia': return { filter: 'sepia(100%)', gradient: null };
            case 'top-fade': return { filter: 'none', gradient: 'linear-gradient(to bottom, rgba(0,0,0,0.6), transparent)' };
            case 'bottom-fade': return { filter: 'none', gradient: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)' };
            case 'left-fade': return { filter: 'none', gradient: 'linear-gradient(to right, rgba(0,0,0,0.6), transparent)' };
            case 'right-fade': return { filter: 'none', gradient: 'linear-gradient(to left, rgba(0,0,0,0.6), transparent)' };
            case 'center-glow': return { filter: 'none', gradient: 'radial-gradient(circle at center, transparent 20%, rgba(0,0,0,0.6))' };
            case 'dark-vignette': return { filter: 'none', gradient: 'radial-gradient(circle, transparent 30%, rgba(0,0,0,0.7))' };
            case 'light-vignette': return { filter: 'none', gradient: 'radial-gradient(circle, transparent 30%, rgba(255,255,255,0.7))' };
            default: return { filter: 'none', gradient: null };
        }
    };

    // Module Style System - Different designs for course modules
    const getModuleStyle = (styleType = 'apple') => {
        switch (styleType) {
            case 'apple':
                return {
                    container: {
                        padding: '32px',
                        background: `linear-gradient(145deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.95) 50%, rgba(248, 250, 252, 0.9) 100%)`,
                        borderRadius: '24px',
                        border: '1px solid rgba(0, 0, 0, 0.04)',
                        boxShadow: `0 20px 40px rgba(0, 0, 0, 0.06), 0 8px 16px rgba(0, 0, 0, 0.04), 0 4px 8px rgba(0, 0, 0, 0.02), inset 0 1px 0 rgba(255, 255, 255, 0.9)`,
                        backdropFilter: 'blur(16px) saturate(160%)',
                        color: '#374151'
                    },
                    badge: { backgroundColor: 'rgba(16, 185, 129, 0.08)', borderColor: 'rgba(16, 185, 129, 0.12)', color: '#059669' },
                    numberCircle: { backgroundColor: '#10b981', color: 'white', boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)' }
                };
            
            case 'gradient':
                return {
                    container: {
                        padding: '32px',
                        background: `linear-gradient(135deg, #667eea 0%, #764ba2 100%)`,
                        borderRadius: '20px',
                        border: 'none',
                        boxShadow: `0 16px 32px rgba(102, 126, 234, 0.3), 0 8px 16px rgba(118, 75, 162, 0.2)`,
                        color: 'white'
                    },
                    badge: { backgroundColor: 'rgba(255, 255, 255, 0.2)', borderColor: 'rgba(255, 255, 255, 0.3)', color: 'white' },
                    numberCircle: { backgroundColor: 'rgba(255, 255, 255, 0.9)', color: '#667eea', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)' }
                };
            
            case 'neon':
                return {
                    container: {
                        padding: '32px',
                        background: `linear-gradient(145deg, #0f0f23 0%, #1a1a2e 100%)`,
                        borderRadius: '16px',
                        border: '1px solid #00ffff',
                        boxShadow: `0 0 20px rgba(0, 255, 255, 0.3), 0 0 40px rgba(0, 255, 255, 0.1), inset 0 1px 0 rgba(0, 255, 255, 0.2)`,
                        color: '#ffffff'
                    },
                    badge: { backgroundColor: 'rgba(0, 255, 255, 0.1)', borderColor: 'rgba(0, 255, 255, 0.3)', color: '#00ffff' },
                    numberCircle: { backgroundColor: '#00ffff', color: '#0f0f23', boxShadow: '0 0 10px rgba(0, 255, 255, 0.5)' }
                };
            
            case 'warm':
                return {
                    container: {
                        padding: '32px',
                        background: `linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)`,
                        borderRadius: '24px',
                        border: '1px solid rgba(252, 182, 159, 0.3)',
                        boxShadow: `0 16px 32px rgba(252, 182, 159, 0.2), 0 8px 16px rgba(255, 236, 210, 0.3)`,
                        color: '#8b4513'
                    },
                    badge: { backgroundColor: 'rgba(139, 69, 19, 0.1)', borderColor: 'rgba(139, 69, 19, 0.2)', color: '#8b4513' },
                    numberCircle: { backgroundColor: '#ff6b35', color: 'white', boxShadow: '0 2px 8px rgba(255, 107, 53, 0.4)' }
                };
            
            case 'minimal':
                return {
                    container: {
                        padding: '24px',
                        background: `#ffffff`,
                        borderRadius: '12px',
                        border: '2px solid #e2e8f0',
                        boxShadow: `0 4px 6px rgba(0, 0, 0, 0.05)`,
                        color: '#1f2937'
                    },
                    badge: { backgroundColor: '#f1f5f9', borderColor: '#cbd5e1', color: '#475569' },
                    numberCircle: { backgroundColor: '#3b82f6', color: 'white', boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)' }
                };
            
            case 'glass':
                return {
                    container: {
                        padding: '32px',
                        background: `rgba(255, 255, 255, 0.1)`,
                        borderRadius: '20px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        boxShadow: `0 8px 32px rgba(0, 0, 0, 0.1)`,
                        backdropFilter: 'blur(20px) saturate(180%)',
                        color: '#1f2937'
                    },
                    badge: { backgroundColor: 'rgba(99, 102, 241, 0.1)', borderColor: 'rgba(99, 102, 241, 0.2)', color: '#6366f1' },
                    numberCircle: { backgroundColor: 'rgba(99, 102, 241, 0.9)', color: 'white', boxShadow: '0 2px 8px rgba(99, 102, 241, 0.4)' }
                };
            
            default:
                return getModuleStyle('apple');
        }
    };

    // Apple-Style Premium Widget - Luxurious & Minimal
    const widgetStyle = {
        backgroundColor: data.backgroundColor,
        borderRadius: '28px',
        marginTop: `${data.marginTop}px`,
        marginBottom: `${data.marginBottom}px`,
        padding: isMobileLayout ? '40px 32px' : '64px 56px',
        border: getBorderStyle() !== 'none' ? getBorderStyle() : '1px solid rgba(0, 0, 0, 0.04)',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: `
            0 32px 64px rgba(0, 0, 0, 0.08),
            0 16px 32px rgba(0, 0, 0, 0.04),
            0 8px 16px rgba(0, 0, 0, 0.02),
            inset 0 1px 0 rgba(255, 255, 255, 0.8)
        `,
        minHeight: data.layout === 'hero' ? `${data.height}px` : 'auto',
        cursor: isEditMode ? 'pointer' : 'default',
        transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        backdropFilter: 'blur(20px) saturate(180%)',
        maxWidth: '1400px',
        width: '100%',
        margin: `${data.marginTop}px auto ${data.marginBottom}px auto`,
        background: `
            linear-gradient(135deg, 
                rgba(255, 255, 255, 0.95) 0%, 
                rgba(255, 255, 255, 0.9) 50%, 
                rgba(248, 250, 252, 0.85) 100%
            )
        `
    };

    // Loading state
    if (loading) {
        return (
            <div style={widgetStyle} ref={widgetRef}>
                {isEditMode && (
                    <button className="delete_button" onClick={() => setShowDeleteModal(true)}>
                        <img className="delete_logo" src={deleteLogo} alt="Löschen" />
                    </button>
                )}
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    minHeight: '200px',
                    color: '#64748b' 
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                            width: '20px',
                            height: '20px',
                            border: '2px solid #e2e8f0',
                            borderTop: '2px solid #10b981',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                        }}></div>
                        <span>Loading course...</span>
                    </div>
                </div>
            </div>
        );
    }

    // No Course Selected State
    if (!data.courseId || !courseData) {
        return (
            <div className="course-info-widget no-course-selected" style={widgetStyle} ref={widgetRef}>
                {isEditMode && (
                    <div>
                        <button className="delete_button" onClick={() => setShowDeleteModal(true)}>
                            <img className="delete_logo" src={deleteLogo} alt="Löschen" />
                        </button>
                        {showDeleteModal && (
                            <DeleteModal
                                isOpen={showDeleteModal}
                                onClose={() => setShowDeleteModal(false)}
                                onConfirm={handleDelete}
                            />
                        )}
                    </div>
                )}
                <div className="course-selection-guide">
                    <div className="guide-icon">
                        <div className="icon-circle" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
                            <i className="fas fa-graduation-cap"></i>
                        </div>
                        <div className="pulse-ring" style={{ borderColor: '#10b981' }}></div>
                    </div>
                    <div className="guide-content">
                        <h3 className="guide-title" style={{ background: 'linear-gradient(135deg, #1e293b 0%, #10b981 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Select Your Course</h3>
                        <p className="guide-description">
                            Choose a video course from your collection to showcase it beautifully with this widget.
                        </p>
                        <div className="guide-steps">
                            <div className="step">
                                <span className="step-number" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>1</span>
                                <span className="step-text">Open the Course tab in the widget settings</span>
                            </div>
                            <div className="step">
                                <span className="step-number" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>2</span>
                                <span className="step-text">Select your course from the dropdown</span>
                            </div>
                            <div className="step">
                                <span className="step-number" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>3</span>
                                <span className="step-text">Customize the display options</span>
                            </div>
                        </div>
                        <div className="guide-note" style={{ background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)', borderColor: '#10b981' }}>
                            <i className="fas fa-info-circle" style={{ color: '#059669' }}></i>
                            <span>This widget works with all your Video Courses from the Sales Platform</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const courseTypeInfo = getCourseTypeInfo(courseData.subtype);
    const totalDuration = calculateTotalDuration(courseData.contentData?.videoModules);

    return (
        <div 
            className="course-info-widget"
            style={widgetStyle} 
            ref={widgetRef}
            onClick={() => isEditMode && onSelectWidget && onSelectWidget(data.id)}
        >
            {/* Background Image Container with Effects */}
            {data.backgroundImage && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 1
                }}>
                    <img 
                        src={data.backgroundImage}
                        alt="Background"
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            opacity: data.backgroundImageOpacity > 1 ? 
                                (parseFloat(data.backgroundImageOpacity) / 100) : // Prozent-Wert (10-100) zu Dezimal
                                (parseFloat(data.backgroundImageOpacity) || 0.85), // Bereits Dezimal-Wert (0.1-1.0)
                            filter: data.imageEffect ? getImageEffect(data.imageEffect).filter : 'none'
                        }}
                        onError={(e) => {
                            console.log('Background image failed to load:', data.backgroundImage);
                            e.target.style.display = 'none';
                        }}
                        onLoad={() => {
                            console.log('Background image loaded successfully:', data.backgroundImage);
                            console.log('Background opacity value:', data.backgroundImageOpacity, 'converted to:', 
                                data.backgroundImageOpacity >= 1 ? 
                                    (parseFloat(data.backgroundImageOpacity) / 100) : 
                                    (parseFloat(data.backgroundImageOpacity) || 0.85)
                            );
                        }}
                    />
                    
                    {/* Gradient/Effect Overlay */}
                    {data.imageEffect && getImageEffect(data.imageEffect).gradient && (
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: getImageEffect(data.imageEffect).gradient
                        }} />
                    )}
                </div>
            )}
            
            {/* Delete Button */}
            {isEditMode && (
                <button 
                    className="delete_button" 
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowDeleteModal(true);
                    }}
                    style={{ zIndex: 10 }}
                >
                    <img className="delete_logo" src={deleteLogo} alt="Löschen" />
                </button>
            )}

            {/* Delete Modal */}
            {isEditMode && showDeleteModal && (
                <DeleteModal
                    isOpen={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                    onConfirm={handleDelete}
                />
            )}

            {/* Premium 2-Column Layout */}
            <div style={{ 
                position: 'relative', 
                zIndex: 2, 
                width: '100%',
                display: 'grid',
                gridTemplateColumns: isMobileLayout ? '1fr' : 
                                   data.layout === 'inline' && data.showCourseImage && data.courseImage ? '1fr 400px' :
                                   '1fr',
                gap: isMobileLayout ? '32px' : '48px',
                alignItems: 'start'
            }}>
                
                {/* Left Column - Main Content */}
                <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '40px'
                }}>
                    
                    {/* Premium Header Section */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '24px'
                    }}>
                        {/* Course Type Badge */}
                        {data.showCourseType && courseData.subtype && (
                            <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '10px',
                                backgroundColor: `${courseTypeInfo.color}08`,
                                color: courseTypeInfo.color,
                                padding: '10px 16px',
                                borderRadius: '12px',
                                fontSize: `${data.courseTypeSize + 1}px`,
                                fontWeight: '600',
                                width: 'fit-content',
                                border: `1px solid ${courseTypeInfo.color}15`,
                                backdropFilter: 'blur(8px)',
                                letterSpacing: '0.3px'
                            }}>
                                <i className={courseTypeInfo.icon} style={{ fontSize: '16px' }}></i>
                                <span>{courseTypeInfo.label}</span>
                            </div>
                        )}

                        {/* Title - Uses Course Data (NOT editable inline) */}
                        {data.showTitle && (
                            <h1
                                style={{
                                    ...getResponsiveStyle('titleSize'),
                                    fontWeight: getResponsiveStyle('titleWeight', 'Weight'),
                                    color: data.titleColor,
                                    fontFamily: data.titleFont || fonts.globalBigFont,
                                    margin: '0',
                                    lineHeight: '1.1',
                                    letterSpacing: '-0.02em',
                                    ...getTextEffectStyles(data.titleEffect)
                                }}
                            >
                                {courseData.title}
                            </h1>
                        )}

                        {/* Description - Uses Course Data (NOT editable inline) */}
                        {data.showDescription && (
                            <p
                                style={{
                                    ...getResponsiveStyle('descriptionSize'),
                                    fontWeight: getResponsiveStyle('descriptionWeight', 'Weight'),
                                    color: data.descriptionColor,
                                    fontFamily: data.descriptionFont || fonts.globalSmallFont,
                                    margin: '0',
                                    lineHeight: '1.7',
                                    opacity: 0.85,
                                    ...getTextEffectStyles(data.descriptionEffect)
                                }}
                            >
                                {courseData.description}
                            </p>
                        )}

                        {/* Course Duration Info */}
                        {data.showDuration && (
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '16px',
                                padding: '20px 24px',
                                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                                borderRadius: '16px',
                                border: '1px solid rgba(0, 0, 0, 0.06)',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                                backdropFilter: 'blur(8px)'
                            }}>
                                <div style={{
                                    width: '48px',
                                    height: '48px',
                                    borderRadius: '12px',
                                    backgroundColor: `${data.durationColor}12`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '18px',
                                    border: `1px solid ${data.durationColor}20`,
                                    color: data.durationColor
                                }}>
                                    <i className="fas fa-clock"></i>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                                    <span style={{
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        color: data.durationColor,
                                        opacity: 0.7,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.8px'
                                    }}>
                                        {data.showDurationTitle !== false && (data.durationTitle || 'Total Duration')}
                                    </span>
                                    <span style={{
                                        fontSize: `${data.durationSize + 3}px`,
                                        fontWeight: '500',
                                        color: data.durationColor,
                                        lineHeight: '1.4'
                                    }}>
                                        {totalDuration}
                                        {courseData.contentData?.videoModules?.length && (
                                            <span style={{ opacity: 0.7, fontSize: '14px', marginLeft: '8px' }}>
                                                • {courseData.contentData.videoModules.length} modules
                                            </span>
                                        )}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Premium Course Modules */}
                    {data.showModules && courseData.contentData?.videoModules?.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                <div style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '12px',
                                    backgroundColor: `${data.moduleColor}12`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: `1px solid ${data.moduleColor}20`,
                                    color: data.moduleColor
                                }}>
                                    <i className="fas fa-play-circle" style={{ fontSize: '16px' }}></i>
                                </div>
                                <h2 
                                    style={{ 
                                        margin: '0', 
                                        fontSize: '24px', 
                                        fontWeight: '700',
                                        color: data.moduleColor,
                                        letterSpacing: '-0.02em',
                                        cursor: isEditMode ? 'text' : 'default',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onClick={() => isEditMode && handleInlineEdit('modulesTitle', data.modulesTitle || 'Course Modules')}
                                    title={isEditMode ? "Click to edit" : undefined}
                                >
                                    {data.showModulesTitle !== false && (
                                        isInlineEditing === 'modulesTitle' ? (
                                            <input
                                                type="text"
                                                value={inlineValue}
                                                onChange={(e) => setInlineValue(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && handleInlineSave()}
                                                onBlur={handleInlineSave}
                                                style={{
                                                    fontSize: '24px',
                                                    fontWeight: '700',
                                                    color: data.moduleColor,
                                                    letterSpacing: '-0.02em',
                                                    background: 'transparent',
                                                    border: 'none',
                                                    outline: 'none',
                                                    margin: '0',
                                                    padding: '0',
                                                    width: '100%',
                                                    fontFamily: 'inherit'
                                                }}
                                                autoFocus
                                            />
                                        ) : (
                                            data.modulesTitle || 'Course Modules'
                                        )
                                    )}
                                </h2>
                            </div>
                            
                            <div style={{ 
                                display: 'grid',
                                gridTemplateColumns: isMobileLayout ? '1fr' : 'repeat(auto-fit, minmax(380px, 1fr))',
                                gap: '32px'
                            }}>
                                {courseData.contentData.videoModules.slice(0, data.maxModulesToShow || 6).map((module, index) => {
                                    const moduleStyle = getModuleStyle(data.moduleStyle || 'apple');
                                    return (
                                        <div key={index} className="course-module-card" style={{
                                            ...moduleStyle.container,
                                            transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                                            position: 'relative',
                                            overflow: 'hidden'
                                        }}>
                                            {/* Module Header */}
                                            <div style={{ 
                                                display: 'flex', 
                                                justifyContent: 'space-between', 
                                                alignItems: 'flex-start', 
                                                marginBottom: '24px',
                                                gap: '20px'
                                            }}>
                                                <div style={{ flex: 1 }}>
                                                    {/* Module Number - Dynamic Style */}
                                                    <div style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '10px',
                                                        marginBottom: '12px',
                                                        padding: '6px 14px',
                                                        backgroundColor: moduleStyle.badge.backgroundColor,
                                                        borderRadius: '20px',
                                                        border: `1px solid ${moduleStyle.badge.borderColor}`
                                                    }}>
                                                        <div style={{
                                                            width: '20px',
                                                            height: '20px',
                                                            borderRadius: '50%',
                                                            backgroundColor: moduleStyle.numberCircle.backgroundColor,
                                                            color: moduleStyle.numberCircle.color,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: '10px',
                                                            fontWeight: '700',
                                                            boxShadow: moduleStyle.numberCircle.boxShadow
                                                        }}>
                                                            {index + 1}
                                                        </div>
                                                        <span style={{
                                                            fontSize: '11px',
                                                            fontWeight: '600',
                                                            color: moduleStyle.badge.color,
                                                            letterSpacing: '0.3px',
                                                            textTransform: 'uppercase'
                                                        }}>
                                                            Module {index + 1}
                                                        </span>
                                                    </div>
                                                    
                                                    {/* Module Title - Dynamic Typography */}
                                                    <h3 style={{ 
                                                        margin: '0', 
                                                        fontSize: `${data.moduleSize + 8}px`,
                                                        fontWeight: '700',
                                                        color: moduleStyle.container.color || data.titleColor,
                                                        letterSpacing: '-0.02em',
                                                        lineHeight: '1.2',
                                                        fontFamily: data.titleFont || fonts.globalBigFont
                                                    }}>
                                                        {module.title || `Module ${index + 1}`}
                                                    </h3>
                                                </div>
                                                
                                                {/* Duration Badge - Apple Style */}
                                                {module.duration && (
                                                    <div style={{
                                                        padding: '8px 16px',
                                                        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(99, 102, 241, 0.05) 100%)',
                                                        borderRadius: '12px',
                                                        fontSize: '13px',
                                                        fontWeight: '600',
                                                        color: data.durationColor,
                                                        border: '1px solid rgba(99, 102, 241, 0.15)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        backdropFilter: 'blur(8px)',
                                                        boxShadow: '0 4px 8px rgba(99, 102, 241, 0.1)'
                                                    }}>
                                                        <i className="fas fa-clock" style={{ fontSize: '11px' }}></i>
                                                        {formatModuleDuration(module.duration)}
                                                    </div>
                                                )}
                                            </div>
                                            
                                            {/* Module Description - Dynamic Typography */}
                                            {data.showModuleDetails && module.description && (
                                                <p style={{ 
                                                    margin: '0 0 24px 0', 
                                                    fontSize: '16px',
                                                    color: moduleStyle.container.color || data.descriptionColor,
                                                    opacity: 0.75,
                                                    lineHeight: '1.65',
                                                    fontWeight: '400',
                                                    letterSpacing: '0.01em'
                                                }}>
                                                    {module.description}
                                                </p>
                                            )}
                                            
                                            {/* Apple-Style Learning Outcomes */}
                                            {data.showFeaturePoints !== false && module.featurePoints?.length > 0 && (
                                                <div style={{
                                                    marginTop: '20px',
                                                    padding: '24px',
                                                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.04) 0%, rgba(139, 92, 246, 0.02) 100%)',
                                                    borderRadius: '16px',
                                                    border: '1px solid rgba(99, 102, 241, 0.12)',
                                                    backdropFilter: 'blur(12px)'
                                                }}>
                                                    <div style={{
                                                        fontSize: '12px',
                                                        fontWeight: '600',
                                                        color: '#6366f1',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.8px',
                                                        marginBottom: '16px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px'
                                                    }}>
                                                        <i className="fas fa-graduation-cap" style={{ fontSize: '11px' }}></i>
                                                        What You'll Learn
                                                    </div>
                                                    <div style={{ 
                                                        display: 'grid',
                                                        gridTemplateColumns: isMobileLayout ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
                                                        gap: '12px'
                                                    }}>
                                                        {module.featurePoints.map((feature, featureIndex) => (
                                                            <div key={featureIndex} style={{
                                                                padding: '12px 16px',
                                                                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.7) 100%)',
                                                                borderRadius: '12px',
                                                                fontSize: '14px',
                                                                color: '#374151',
                                                                fontWeight: '500',
                                                                display: 'flex',
                                                                alignItems: 'flex-start',
                                                                gap: '10px',
                                                                border: '1px solid rgba(0, 0, 0, 0.04)',
                                                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                                                                transition: 'all 0.3s ease'
                                                            }}>
                                                                <div style={{
                                                                    width: '24px',
                                                                    height: '24px',
                                                                    borderRadius: '50%',
                                                                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    flexShrink: 0,
                                                                    boxShadow: '0 2px 6px rgba(99, 102, 241, 0.3)'
                                                                }}>
                                                                    <i className={feature.icon} style={{ 
                                                                        fontSize: '10px', 
                                                                        color: 'white' 
                                                                    }}></i>
                                                                </div>
                                                                <span style={{ 
                                                                    lineHeight: '1.5',
                                                                    flex: 1
                                                                }}>
                                                                    {feature.text}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Apple-Style Course Materials */}
                                            {data.showDocuments && module.documents?.length > 0 && (
                                                <div style={{
                                                    marginTop: '20px',
                                                    padding: '20px',
                                                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.03) 0%, rgba(5, 150, 105, 0.02) 100%)',
                                                    borderRadius: '16px',
                                                    border: '1px solid rgba(16, 185, 129, 0.08)',
                                                    backdropFilter: 'blur(8px)'
                                                }}>
                                                    <div style={{
                                                        fontSize: '12px',
                                                        fontWeight: '600',
                                                        color: '#059669',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.8px',
                                                        marginBottom: '12px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px'
                                                    }}>
                                                        <i className="fas fa-folder-open" style={{ fontSize: '11px' }}></i>
                                                        {data.showMaterialsTitle !== false && (data.materialsTitle || 'Course Materials')}
                                                    </div>
                                                    <div style={{ 
                                                        display: 'grid',
                                                        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                                                        gap: '8px'
                                                    }}>
                                                        {module.documents.map((doc, docIndex) => (
                                                            <div key={docIndex} style={{
                                                                padding: '8px 12px',
                                                                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(248, 250, 252, 0.6) 100%)',
                                                                borderRadius: '8px',
                                                                fontSize: '11px',
                                                                color: '#374151',
                                                                fontWeight: '500',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '6px',
                                                                border: '1px solid rgba(0, 0, 0, 0.04)',
                                                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)'
                                                            }}>
                                                                <i className="fas fa-file-pdf" style={{ fontSize: '9px', color: '#ef4444' }}></i>
                                                                <span style={{ 
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap'
                                                                }}>
                                                                    {doc.name || `Resource ${docIndex + 1}`}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Premium Custom Features */}
                    {data.showCustomFeatures && data.customFeatures?.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '8px',
                                    backgroundColor: `${data.featuresIconColor}15`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: data.featuresIconColor
                                }}>
                                    <i className="fas fa-star" style={{ fontSize: '14px' }}></i>
                                </div>
                                <h3 
                                    style={{ 
                                        margin: '0', 
                                        fontSize: '20px', 
                                        fontWeight: '600',
                                        color: data.titleColor,
                                        letterSpacing: '-0.01em',
                                        cursor: isEditMode ? 'text' : 'default',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onClick={() => isEditMode && handleInlineEdit('featuresTitle', data.featuresTitle || 'What\'s Included')}
                                    title={isEditMode ? "Click to edit" : undefined}
                                >
                                    {data.showFeaturesTitle !== false && (
                                        isInlineEditing === 'featuresTitle' ? (
                                            <input
                                                type="text"
                                                value={inlineValue}
                                                onChange={(e) => setInlineValue(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && handleInlineSave()}
                                                onBlur={handleInlineSave}
                                                style={{
                                                    fontSize: '20px',
                                                    fontWeight: '600',
                                                    color: data.titleColor,
                                                    letterSpacing: '-0.01em',
                                                    background: 'transparent',
                                                    border: 'none',
                                                    outline: 'none',
                                                    margin: '0',
                                                    padding: '0',
                                                    width: '100%',
                                                    fontFamily: 'inherit'
                                                }}
                                                autoFocus
                                            />
                                        ) : (
                                            data.featuresTitle || 'What\'s Included'
                                        )
                                    )}
                                </h3>
                            </div>
                            
                            <div style={{ 
                                display: data.featuresLayout === 'column' ? 'flex' : 'grid',
                                flexDirection: data.featuresLayout === 'column' ? 'column' : undefined,
                                gridTemplateColumns: data.featuresLayout === 'column' ? undefined : 
                                                   (isMobileLayout ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))'),
                                gap: data.featuresLayout === 'column' ? '12px' : '16px'
                            }}>
                                {data.customFeatures.map((feature, index) => (
                                    <div key={index} style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '16px',
                                        padding: data.featuresLayout === 'column' ? '12px 16px' : '16px 20px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.6)',
                                        borderRadius: data.featuresLayout === 'column' ? '8px' : '12px',
                                        border: '1px solid rgba(0, 0, 0, 0.06)',
                                        boxShadow: data.featuresLayout === 'column' ? 
                                                  '0 1px 4px rgba(0, 0, 0, 0.04)' : 
                                                  '0 2px 8px rgba(0, 0, 0, 0.04)',
                                        transition: 'all 0.2s ease',
                                        width: data.featuresLayout === 'column' ? '100%' : undefined
                                    }}>
                                        <div style={{
                                            width: '28px',
                                            height: '28px',
                                            borderRadius: '50%',
                                            backgroundColor: data.featuresIconColor,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            boxShadow: `0 2px 8px ${data.featuresIconColor}40`
                                        }}>
                                            <i className="fas fa-check" style={{ 
                                                color: 'white', 
                                                fontSize: '12px',
                                                fontWeight: '700'
                                            }}></i>
                                        </div>
                                        <span style={{
                                            fontSize: '15px',
                                            color: data.descriptionColor,
                                            fontWeight: '500',
                                            lineHeight: '1.4'
                                        }}>
                                            {feature.text}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Premium Enroll Button */}
                    {data.showButton && (
                        <div style={{ 
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: data.contentAlignment === 'center' ? 'center' :
                                       data.contentAlignment === 'right' ? 'flex-end' : 'flex-start',
                            marginTop: '8px',
                            gap: '12px'
                        }}>
                            <button
                                onClick={() => {
                                    if (isEditMode) return;
                                    
                                    // Debug info before any action
                                    console.log('=== COURSE BUTTON CLICK DEBUG ===');
                                    console.log('courseData:', courseData);
                                    console.log('data.buttonLink:', data.buttonLink);
                                    console.log('data.useStripeCheckout:', data.useStripeCheckout);
                                    console.log('isFreeProduct():', isFreeProduct());
                                    console.log('courseData?.stripePriceId:', courseData?.stripePriceId);
                                    console.log('===========================');
                                    
                                    if (data.buttonLink) {
                                        console.log('→ Opening custom button link');
                                        window.open(data.buttonLink, '_blank');
                                    } else if (courseData && isFreeProduct() && courseData.type === 'course') {
                                        // Free course - show form popup
                                        console.log('→ Opening form popup for free course');
                                        const event = new CustomEvent('triggerProductFormPopup', {
                                            detail: { 
                                                productId: data.courseId,
                                                trigger: 'courseInfoWidget',
                                                productData: courseData
                                            }
                                        });
                                        window.dispatchEvent(event);
                                    } else if (courseData && !isFreeProduct() && data.useStripeCheckout && courseData.enableStripePayment !== false) {
                                        if (courseData.stripePriceId) {
                                            // Paid course with Stripe checkout enabled - use Stripe
                                            console.log('→ Triggering Stripe checkout');
                                            handleStripeCheckout();
                                        } else {
                                            // Demo mode for testing - use fake Stripe data
                                            console.log('→ DEMO MODE: Using test Stripe configuration');
                                            alert('DEMO MODE: Would normally redirect to Stripe Checkout now!\n\nIn real mode, you need to configure:\n- Stripe Product ID (e.g. prod_ABC123)\n- Stripe Price ID (e.g. price_XYZ789)\n\nFor now, simulating Stripe redirect...');
                                        }
                                    } else if (courseData && !isFreeProduct() && courseData.enableFormOnly !== false) {
                                        // Paid course with form-only enabled - show form popup
                                        console.log('→ Course configured for form-only - showing form popup');
                                        const event = new CustomEvent('triggerProductFormPopup', {
                                            detail: { 
                                                productId: data.courseId,
                                                trigger: 'courseInfoWidget',
                                                productData: courseData,
                                                paymentMethod: 'form-only'
                                            }
                                        });
                                        window.dispatchEvent(event);
                                    } else if (courseData && !isFreeProduct() && data.buttonLink) {
                                        // Paid course without Stripe checkout - use custom link
                                        console.log('→ Opening course custom link');
                                        window.open(data.buttonLink, '_blank');
                                    } else {
                                        // Fallback - redirect to course page
                                        console.log('→ Fallback: redirecting to course page');
                                        window.location.href = `/product/${data.courseId}`;
                                    }
                                }}
                                style={{
                                    ...getButtonEffectStyles(data.buttonEffect),
                                    background: `linear-gradient(135deg, ${data.buttonColor} 0%, ${data.buttonColor}dd 100%)`,
                                    color: data.buttonTextColor,
                                    border: `1px solid ${data.buttonColor}20`,
                                    borderRadius: `${data.buttonBorderRadius + 8}px`,
                                    padding: `${data.buttonPaddingY + 8}px ${data.buttonPaddingX + 16}px`,
                                    fontSize: '17px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                                    fontFamily: data.buttonFont || fonts.globalSmallFont,
                                    boxShadow: `
                                        0 8px 24px ${data.buttonColor}30,
                                        0 4px 12px ${data.buttonColor}20,
                                        inset 0 1px 0 rgba(255, 255, 255, 0.3)
                                    `,
                                    letterSpacing: '0.2px',
                                    textTransform: 'none',
                                    minWidth: '200px',
                                    backdropFilter: 'blur(8px)'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.transform = 'translateY(-3px) scale(1.02)';
                                    e.target.style.boxShadow = `
                                        0 12px 32px ${data.buttonColor}40,
                                        0 8px 16px ${data.buttonColor}30,
                                        inset 0 1px 0 rgba(255, 255, 255, 0.4)
                                    `;
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.transform = 'translateY(0) scale(1)';
                                    e.target.style.boxShadow = `
                                        0 8px 24px ${data.buttonColor}30,
                                        0 4px 12px ${data.buttonColor}20,
                                        inset 0 1px 0 rgba(255, 255, 255, 0.3)
                                    `;
                                }}
                            >
                                {isProcessingCheckout ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <div style={{
                                            width: '16px',
                                            height: '16px',
                                            border: '2px solid transparent',
                                            borderTop: '2px solid currentColor',
                                            borderRadius: '50%',
                                            animation: 'spin 1s linear infinite'
                                        }}></div>
                                        Processing...
                                    </div>
                                ) : (
                                    courseData?.price === 0 || courseData?.price === '0' ? 
                                        (data.showFreeCourseButtonText !== false ? (data.freeCourseButtonText || 'Start Free Course') : 'Start Free Course') : 
                                        (data.showBuyCourseButtonText !== false ? (data.buyCourseButtonText || 'Enroll Now') : 'Enroll Now')
                                )}
                            </button>

                            {/* Stripe Badge for Paid Courses */}
                            {courseData && !isFreeProduct() && data.useStripeCheckout && courseData.enableStripePayment !== false && (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    fontSize: '12px',
                                    color: data.stripeTextColor || '#64748b',
                                    fontFamily: fonts.globalSmallFont || 'Arial, sans-serif',
                                    justifyContent: data.contentAlignment === 'center' ? 'center' :
                                                   data.contentAlignment === 'right' ? 'flex-end' : 'flex-start'
                                }}>
                                    <img 
                                        src="/img/stripe.svg" 
                                        alt="Stripe" 
                                        style={{ 
                                            width: '32px', 
                                            height: '32px',
                                            opacity: 0.8
                                        }} 
                                    />
                                    <span>Secure payment with Stripe</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Right Column - Course Image */}
                {!isMobileLayout && (
                    <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '32px',
                        position: 'sticky',
                        top: '20px'
                    }}>
                        
                        {/* Course Image - Premium Display */}
                        {data.showCourseImage && (
                            data.courseImage ? (
                            <div style={{
                                borderRadius: '20px',
                                overflow: 'hidden',
                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                                border: '1px solid rgba(0, 0, 0, 0.06)'
                            }}>
                                <img 
                                    src={data.courseImage}
                                    alt={courseData.title}
                                    style={{
                                        width: '100%',
                                        height: '240px',
                                        objectFit: 'cover',
                                        display: 'block'
                                    }}
                                    onError={(e) => {
                                        console.log('Course image failed to load:', data.courseImage);
                                        e.target.style.display = 'none';
                                    }}
                                    onLoad={() => {
                                        console.log('Course image loaded successfully:', data.courseImage);
                                    }}
                                />
                            </div>
                            ) : (
                                <div style={{
                                    borderRadius: '20px',
                                    padding: '40px',
                                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                    border: '2px dashed rgba(0, 0, 0, 0.1)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '12px',
                                    textAlign: 'center'
                                }}>
                                    <div style={{
                                        width: '48px',
                                        height: '48px',
                                        borderRadius: '12px',
                                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '16px',
                                        color: '#9ca3af'
                                    }}>
                                        <i className="fas fa-image"></i>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', marginBottom: '4px' }}>
                                            No Course Image
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                                            Upload an image in the widget settings
                                        </div>
                                    </div>
                                </div>
                            )
                        )}
                    </div>
                )}
            </div>

            {/* CSS Animations */}
            <style jsx>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                .course-info-widget:hover {
                    transform: translateY(-4px) scale(1.005);
                    box-shadow: 
                        0 40px 80px rgba(0, 0, 0, 0.12),
                        0 20px 40px rgba(0, 0, 0, 0.08),
                        0 8px 16px rgba(0, 0, 0, 0.04),
                        inset 0 1px 0 rgba(255, 255, 255, 0.9);
                }
                
                .course-info-widget .course-module-card:hover {
                    transform: translateY(-2px) scale(1.01);
                    box-shadow: 
                        0 24px 48px rgba(0, 0, 0, 0.08),
                        0 12px 24px rgba(0, 0, 0, 0.06),
                        0 6px 12px rgba(0, 0, 0, 0.04),
                        inset 0 1px 0 rgba(255, 255, 255, 0.95);
                }
            `}</style>
        </div>
    );
};

export default CourseInfoWidget;
